<script setup lang="ts">
/**
 * A tool call the member asks you about before it runs, as every conversation draws it (#244): who wants to run what,
 * exactly what it will run with, and Allow once, Always allow (where the call offers it) and Deny. A decided call's
 * card shows the decision and fades as a decided gate does, then its space closes (CardCloseSpace). The Chat page and
 * Create task both draw it through ChatConversation, so the two cannot drift; deciding stays with the page.
 */
import { Button, type DecisionView, ZhDecisionCard } from '@zerohuman/ui'
import { computed } from 'vue'
import type { ChatMessageRow, ChatToolCallDecision, GateDecision } from '@/api'
import CardCloseSpace from '@/components/CardCloseSpace.vue'
import { confirmationLines, offersAlwaysAllow } from '@/lib/chat'

const props = defineProps<{
    call: ChatMessageRow
    /** The member who asks, as the portal labels them (`memberLabel`). */
    memberName: string
    /** The tool as the thread names it in the current mode, or null when the call names none. */
    toolLabel: string | null
    /** The decision just made on this call, while its card still shows it. */
    decided?: GateDecision
    /** A decision on some call is on its way to the API: every card's buttons wait on it. */
    busy?: boolean
}>()

const emit = defineEmits<{ decide: [decision: ChatToolCallDecision] }>()

/** The card's head: who wants to run what, and why it asks first. */
const decision = computed(
    (): DecisionView => ({
        title: `${props.memberName || 'This member'} wants to run ${props.toolLabel ?? 'a tool'}`,
        why: 'It can change something, so it asks first.',
    }),
)

const lines = computed(() => confirmationLines(props.call))
const offerAlways = computed(() => offersAlwaysAllow(props.call))
const alwaysTitle = computed(() => `Don't ask me again about ${props.toolLabel ?? 'this tool'} for ${props.memberName}`)
const disabled = computed(() => props.busy || props.decided !== undefined)
</script>

<template>
	<CardCloseSpace :decided="decided">
		<ZhDecisionCard data-testid="chat-tool-confirm" variant="inline" :decision="decision" :decided="decided">
			<template #actions>
				<ul
					v-if="lines.length"
					data-testid="chat-tool-confirm-arguments"
					class="mb-2 w-full space-y-1 border p-2 font-mono text-xs break-all"
				>
					<li v-for="line in lines" :key="line">{{ line }}</li>
				</ul>
				<div class="flex w-full flex-wrap items-center gap-2 text-sm">
					<Button type="button" size="sm" :disabled="disabled" @click="emit('decide', 'allow')">Allow once</Button>
					<Button
						v-if="offerAlways"
						type="button"
						size="sm"
						variant="outline"
						:title="alwaysTitle"
						:disabled="disabled"
						@click="emit('decide', 'always')"
					>
						Always allow
					</Button>
					<Button type="button" size="sm" variant="outline" :disabled="disabled" @click="emit('decide', 'deny')">
						Deny
					</Button>
					<span class="text-muted-foreground text-xs">Or reply to decline it.</span>
				</div>
			</template>
		</ZhDecisionCard>
	</CardCloseSpace>
</template>
